import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, FolderSearch, Sparkles, MapPin, BriefcaseBusiness, ChevronDown, Check, Search, Building2, CalendarOff, ArrowLeft, Lock, Users } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { track } from '../lib/analytics';
import { useAuth } from '../context/AuthContext';
import { useEnsureEmbedding } from '../hooks/useEnsureEmbedding';

// Country code → full name mapping
const CODE_TO_COUNTRY = {
    'DE': 'ALLEMAGNE', 'BE': 'BELGIQUE', 'ES': 'ESPAGNE', 'IT': 'ITALIE',
    'PT': 'PORTUGAL', 'CH': 'SUISSE', 'GB': 'ROYAUME-UNI', 'CA': 'CANADA',
    'US': 'ETATS-UNIS', 'AU': 'AUSTRALIE', 'JP': 'JAPON', 'CN': 'CHINE',
    'KR': 'COREE DU SUD', 'SG': 'SINGAPOUR', 'AE': 'EMIRATS ARABES UNIS',
    'MA': 'MAROC', 'SN': 'SENEGAL', 'BR': 'BRESIL', 'MX': 'MEXIQUE',
    'VN': 'VIET NAM', 'IN': 'INDE', 'PL': 'POLOGNE',
    'RO': 'ROUMANIE', 'NL': 'PAYS-BAS', 'HU': 'HONGRIE',
    'CZ': 'REPUBLIQUE TCHEQUE', 'AT': 'AUTRICHE', 'SE': 'SUEDE', 'NO': 'NORVEGE',
    'FI': 'FINLANDE', 'DK': 'DANEMARK', 'IE': 'IRLANDE', 'PH': 'PHILIPPINES',
    'TH': 'THAILANDE', 'ID': 'INDONESIE', 'MY': 'MALAISIE', 'TR': 'TURQUIE',
    'GR': 'GRECE', 'LU': 'LUXEMBOURG', 'CM': 'CAMEROUN', 'CI': "COTE D'IVOIRE",
    'LA': 'LAOS', 'EG': 'EGYPTE', 'ZA': 'AFRIQUE DU SUD', 'CO': 'COLOMBIE',
    'PE': 'PEROU', 'CL': 'CHILI', 'AR': 'ARGENTINE', 'TW': 'TAIWAN',
    'SA': 'ARABIE SAOUDITE', 'QA': 'QATAR', 'KW': 'KOWEIT', 'LB': 'LIBAN',
    'JO': 'JORDANIE', 'MU': 'MAURICE', 'KE': 'KENYA', 'MG': 'MADAGASCAR',
    'CG': 'CONGO', 'CD': 'REPUBLIQUE DEMOCRATIQUE DU CONGO',
    'AO': 'ANGOLA', 'NG': 'NIGERIA', 'TN': 'TUNISIE', 'AZ': 'AZERBAIDJAN'
};

const COUNTRY_TO_CODE = Object.fromEntries(Object.entries(CODE_TO_COUNTRY).map(([k, v]) => [v, k]));

const CODE_TO_FLAG = (code) => {
    if (!code || code.length !== 2) return '🌍';
    return String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E6 - 65 + c.charCodeAt(0)));
};

const getFlag = (countryName) => {
    const code = COUNTRY_TO_CODE[countryName?.toUpperCase()];
    return code ? CODE_TO_FLAG(code) : '🌍';
};

// ─── Searchable Multi-Select ───
const SearchableMultiSelect = ({ value = [], options, onChange, placeholder, preferredValues = [] }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) inputRef.current.focus();
    }, [isOpen]);

    const handleSelect = (val) => {
        const newValue = value.includes(val) ? value.filter(v => v !== val) : [...value, val];
        onChange(newValue);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange([]);
    };

    const filtered = options.filter(opt => {
        const label = opt.label || opt;
        return label.toLowerCase().includes(search.toLowerCase());
    });

    const sorted = [...filtered].sort((a, b) => {
        const aLabel = a.label || a;
        const bLabel = b.label || b;
        const aIsPreferred = preferredValues.includes(a.value || a);
        const bIsPreferred = preferredValues.includes(b.value || b);
        if (aIsPreferred && !bIsPreferred) return -1;
        if (!aIsPreferred && bIsPreferred) return 1;
        return aLabel.localeCompare(bLabel);
    });

    const displayLabel = value.length === 0
        ? placeholder
        : value.length === 1
            ? options.find(o => (o.value || o) === value[0])?.label || value[0]
            : `${value.length} sélectionnés`;

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 text-sm font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:border-blue-300 focus:ring-4 focus:ring-blue-100 transition-all active:scale-[0.98] shadow-sm"
            >
                <span className="truncate">{displayLabel}</span>
                <div className="flex items-center gap-1">
                    {value.length > 0 && (
                        <div
                            onClick={handleClear}
                            className="p-0.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-red-500 transition-colors mr-1"
                        >
                            <X size={14} />
                        </div>
                    )}
                    <ChevronDown size={16} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl shadow-slate-300/30 z-50 overflow-hidden min-w-[200px]">
                    <div className="p-2 border-b border-slate-100">
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Rechercher..."
                                className="w-full pl-8 pr-3 py-2 text-sm text-slate-700 bg-slate-50 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-blue-100 placeholder:text-slate-400"
                            />
                        </div>
                    </div>

                    <div className="max-h-60 overflow-y-auto py-1">
                        {sorted.length === 0 ? (
                            <p className="px-4 py-3 text-sm text-slate-400 text-center">Aucun résultat</p>
                        ) : (
                            sorted.map((opt, i) => {
                                const val = opt.value !== undefined ? opt.value : opt;
                                const label = opt.label !== undefined ? opt.label : opt;
                                const isSelected = value.includes(val);
                                const isPreferred = preferredValues.includes(val);
                                return (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => handleSelect(val)}
                                        className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-all border-b border-slate-50 last:border-0 flex items-center justify-between
                                            ${isSelected
                                                ? 'bg-blue-50 text-blue-600 font-bold'
                                                : isPreferred
                                                    ? 'bg-violet-50/50 text-slate-700 hover:bg-violet-50'
                                                    : 'text-slate-600 hover:bg-slate-50 hover:text-blue-600'
                                            }`}
                                    >
                                        <span className="truncate mr-2 flex items-center gap-2">
                                            {label}
                                            {isPreferred && !isSelected && (
                                                <span className="text-[10px] font-bold text-violet-500 bg-violet-100 px-1.5 py-0.5 rounded-full">favori</span>
                                            )}
                                        </span>
                                        {isSelected && <Check size={14} className="flex-shrink-0" />}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Main Modal ───
const ArchiveSearchModal = ({ isOpen, onClose }) => {
    const { user } = useAuth();
    const { ensureEmbedding, isUpdating: isEmbeddingUpdating } = useEnsureEmbedding();

    // Form state
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedSectors, setSelectedSectors] = useState([]);

    // Options fetched from DB
    const [countryOptions, setCountryOptions] = useState([]);
    const [sectorOptions, setSectorOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(true);

    // User preferred countries
    const [preferredCountryNames, setPreferredCountryNames] = useState([]);

    // Weekly quota
    const [weeklyUsed, setWeeklyUsed] = useState(false); // true if already used this week
    const [checkingQuota, setCheckingQuota] = useState(true);

    // Flow state
    const [step, setStep] = useState('form'); // form | loading | result | error | quota_reached
    const [loadingMessage, setLoadingMessage] = useState('');
    const [matchedOffers, setMatchedOffers] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');

    // Check weekly quota + fetch options
    useEffect(() => {
        if (!isOpen || !user) return;

        const init = async () => {
            setLoadingOptions(true);
            setCheckingQuota(true);

            try {
                // Check weekly quota: count executions from the last 7 days
                const oneWeekAgo = new Date();
                oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

                const { count, error: quotaError } = await supabase
                    .from('archive_executions')
                    .select('*', { count: 'exact', head: true })
                    .eq('user_id', user.id)
                    .gte('executed_at', oneWeekAgo.toISOString());

                if (quotaError) throw quotaError;
                const used = (count || 0) >= 1;
                setWeeklyUsed(used);

                if (used) {
                    setStep('quota_reached');
                    setCheckingQuota(false);
                    setLoadingOptions(false);
                    return;
                }

                // Fetch filter options
                const { data: filterData, error: filterError } = await supabase.rpc('get_archive_filter_options');
                if (filterError) throw filterError;

                const countries = (filterData?.countries || []).filter(c => c && c.trim());
                const sectors = (filterData?.sectors || []).filter(s => s && s.trim());

                setCountryOptions(countries.map(c => ({ value: c, label: c })));
                setSectorOptions(sectors.map(s => ({ value: s, label: s })));

                // Fetch user preferred countries
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('preferred_countries')
                    .eq('id', user.id)
                    .single();

                if (profile?.preferred_countries?.length > 0) {
                    const mapped = profile.preferred_countries.map(code => {
                        if (code && code.length === 2) {
                            const fullName = CODE_TO_COUNTRY[code];
                            return fullName && countries.includes(fullName) ? fullName : null;
                        }
                        return countries.includes(code) ? code : null;
                    }).filter(Boolean);

                    setPreferredCountryNames(mapped);
                    if (mapped.length > 0) setSelectedCountries(mapped);
                }
            } catch (err) {
                console.error('Error initializing archive modal:', err);
            } finally {
                setLoadingOptions(false);
                setCheckingQuota(false);
            }
        };

        init();
    }, [isOpen, user]);

    // Reset when modal closes
    useEffect(() => {
        if (!isOpen) {
            setStep('form');
            setSelectedCountries([]);
            setSelectedSectors([]);
            setMatchedOffers([]);
            setErrorMessage('');
            setPreferredCountryNames([]);
            setWeeklyUsed(false);
            setCheckingQuota(true);
        }
    }, [isOpen]);

    const handleSubmit = async () => {
        if (selectedCountries.length === 0) return;

        setStep('loading');
        setLoadingMessage('Mise à jour de votre profil IA...');

        try {
            const embedding = await ensureEmbedding();
            if (!embedding) {
                throw new Error("Impossible de générer votre profil IA. Veuillez uploader un CV dans votre profil.");
            }

            setLoadingMessage('Recherche d\'offres correspondantes...');

            const { data, error } = await supabase.rpc('cv_matching_archives', {
                embedding: embedding,
                match_threshold: 0.1,
                match_count: 10,
                p_countries: selectedCountries,
                p_sectors: selectedSectors.length > 0 ? selectedSectors : null,
                p_user_id: user.id
            });

            if (error) throw error;

            track('feature_used', { feature: 'archive_search', results_count: data?.length || 0 });

            if (!data || data.length === 0) {
                throw new Error("Aucune offre archivée trouvée pour ces critères. Essayez avec d'autres pays ou secteurs.");
            }

            // Record the execution (1 per week)
            await supabase.from('archive_executions').insert({ user_id: user.id });

            // Record seen offers (so they won't appear again)
            const seenRecords = data.map(offer => ({
                user_id: user.id,
                id_offre: offer.id_offre
            }));
            await supabase.from('archive_seen_offers').upsert(seenRecords, { onConflict: 'user_id,id_offre' });

            setMatchedOffers(data);
            setStep('result');

        } catch (err) {
            console.error('Archive search error:', err);
            setErrorMessage(err.message || "Une erreur est survenue.");
            setStep('error');
        }
    };

    const parseDeactivationDate = (urlLive) => {
        if (!urlLive || urlLive === 'active') return null;
        const match = urlLive.match(/(\d{2}\/\d{2}\/\d{4})/);
        return match ? match[1] : urlLive;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            <div className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full animate-in fade-in zoom-in-95 duration-200 border border-white/20 overflow-hidden max-h-[90vh] flex flex-col">

                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
                >
                    <X size={20} />
                </button>

                {/* ====== QUOTA REACHED STATE ====== */}
                {step === 'quota_reached' && (
                    <div className="p-6 pt-8 flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mb-4 border border-amber-100">
                            <Lock size={32} className="text-amber-500" />
                        </div>
                        <h3 className="text-xl font-black text-slate-900 tracking-tight mb-2">
                            Exploration utilisée
                        </h3>
                        <p className="text-slate-500 text-sm font-medium mb-2 max-w-xs leading-relaxed">
                            Vous avez déjà utilisé votre exploration hebdomadaire.
                            Revenez la semaine prochaine pour découvrir de nouvelles offres archivées !
                        </p>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-full border border-amber-100 mb-6">
                            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                            <span className="text-xs font-bold text-amber-700">0/1 exploration restante</span>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-full py-3 rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
                        >
                            Compris
                        </button>
                    </div>
                )}

                {/* ====== FORM STATE ====== */}
                {step === 'form' && !checkingQuota && (
                    <div className="p-6 pt-8 overflow-y-auto">
                        <div className="flex flex-col items-center text-center mb-6">
                            <div className="w-16 h-16 bg-gradient-to-tr from-violet-100 to-blue-50 rounded-2xl flex items-center justify-center mb-4 shadow-inner rotate-3">
                                <FolderSearch size={32} className="text-violet-600" />
                            </div>
                            <h3 className="text-xl font-black text-slate-900 tracking-tight mb-1">
                                Explorer les archives
                            </h3>
                            <p className="text-slate-500 text-sm font-medium leading-relaxed max-w-xs">
                                Retrouvez d'anciens VIE dans votre secteur pour les contacter et en apprendre plus sur leur expérience
                            </p>
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-full border border-green-100 mt-3">
                                <div className="w-2 h-2 rounded-full bg-green-400" />
                                <span className="text-xs font-bold text-green-700">1/1 exploration disponible</span>
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                                <MapPin size={14} className="text-blue-500" />
                                Pays ciblés
                                <span className="text-red-400 text-xs">*obligatoire</span>
                            </label>
                            {loadingOptions ? (
                                <div className="flex items-center justify-center py-4">
                                    <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                                </div>
                            ) : (
                                <SearchableMultiSelect
                                    value={selectedCountries}
                                    options={countryOptions}
                                    onChange={setSelectedCountries}
                                    placeholder="Sélectionner un ou plusieurs pays"
                                    preferredValues={preferredCountryNames}
                                />
                            )}
                        </div>

                        <div className="mb-6">
                            <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                                <BriefcaseBusiness size={14} className="text-violet-500" />
                                Secteurs d'activité
                                <span className="text-slate-400 text-xs font-medium">optionnel</span>
                            </label>
                            {loadingOptions ? (
                                <div className="flex items-center justify-center py-4">
                                    <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                                </div>
                            ) : (
                                <SearchableMultiSelect
                                    value={selectedSectors}
                                    options={sectorOptions}
                                    onChange={setSelectedSectors}
                                    placeholder="Tous secteurs (optionnel)"
                                />
                            )}
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={selectedCountries.length === 0 || isEmbeddingUpdating}
                            className={`w-full py-4 rounded-xl font-bold text-lg transition-all transform active:scale-[0.98] shadow-xl relative overflow-hidden ${selectedCountries.length === 0
                                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                                    : 'bg-slate-900 text-white hover:bg-slate-800 shadow-slate-900/20'
                                }`}
                        >
                            <div className="flex items-center justify-center gap-2 relative z-10">
                                <Sparkles size={20} />
                                <span>Lancer l'exploration</span>
                            </div>
                        </button>

                        <p className="text-xs text-center text-slate-400 mt-3 font-medium">
                            10 offres archivées aléatoires correspondant à votre profil. 1 exploration par semaine.
                        </p>
                    </div>
                )}

                {/* ====== LOADING STATE ====== */}
                {(step === 'loading' || (step === 'form' && checkingQuota)) && (
                    <div className="p-8 flex flex-col items-center text-center min-h-[300px] justify-center">
                        <div className="relative mb-6">
                            <div className="w-20 h-20 bg-gradient-to-tr from-violet-100 to-blue-100 rounded-3xl flex items-center justify-center animate-pulse">
                                <Sparkles size={36} className="text-violet-600" />
                            </div>
                        </div>
                        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-4" />
                        <p className="text-slate-600 font-bold text-base mb-1">
                            {checkingQuota ? 'Vérification...' : 'Exploration en cours...'}
                        </p>
                        <p className="text-slate-400 text-sm font-medium">
                            {checkingQuota ? 'Vérification de votre quota' : loadingMessage}
                        </p>
                    </div>
                )}

                {/* ====== RESULT STATE ====== */}
                {step === 'result' && (
                    <div className="flex flex-col h-full max-h-[85vh]">
                        {/* Header */}
                        <div className="p-6 pb-4 border-b border-slate-100 flex-shrink-0">
                            <div className="flex items-center gap-3 mb-2">
                                <div>
                                    <h3 className="text-lg font-black text-slate-900 tracking-tight">
                                        Vos offres archivées
                                    </h3>
                                    <p className="text-xs text-slate-500 font-medium mt-1 flex items-center gap-1.5">
                                        <Users size={12} className="text-violet-500" />
                                        Contactez ces VIE pour en savoir plus sur leur poste et d'éventuelles opportunités
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Offers List */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {matchedOffers.map((offer, idx) => {
                                const flag = getFlag(offer.pays);
                                const deactivationDate = parseDeactivationDate(offer.url_live);

                                return (
                                    <div
                                        key={offer.id_offre || idx}
                                        className="bg-white border border-slate-100 rounded-2xl p-4 hover:border-blue-200 hover:shadow-md transition-all group"
                                    >
                                        <div className="flex items-start gap-3">
                                            {/* Flag */}
                                            <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-xl flex-shrink-0 border border-slate-100 group-hover:border-blue-100 transition-colors">
                                                {flag}
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-bold text-sm text-slate-900 leading-tight mb-1 line-clamp-2">
                                                    {offer.intitule_poste || 'Poste non renseigné'}
                                                </h4>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Building2 size={12} className="text-slate-400 flex-shrink-0" />
                                                    <span className="text-xs font-semibold text-slate-600 truncate">
                                                        {offer.entreprise || 'Entreprise inconnue'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3 flex-wrap">
                                                    <span className="text-[11px] font-medium text-slate-500 flex items-center gap-1">
                                                        <MapPin size={10} className="text-slate-400" />
                                                        {offer.pays || '—'}
                                                    </span>
                                                    {deactivationDate && (
                                                        <span className="text-[11px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full flex items-center gap-1 border border-amber-100">
                                                            <CalendarOff size={10} />
                                                            {deactivationDate}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="p-4 pt-3 border-t border-slate-100 flex-shrink-0">
                            <p className="text-xs text-center text-slate-400 font-medium mb-3">
                                Prochaine exploration disponible dans 7 jours
                            </p>
                            <button
                                onClick={onClose}
                                className="w-full py-3 rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20 text-sm"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                )}

                {/* ====== ERROR STATE ====== */}
                {step === 'error' && (
                    <div className="p-6 pt-8 flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-4">
                            <X size={32} className="text-red-500" />
                        </div>
                        <h3 className="text-xl font-black text-slate-900 tracking-tight mb-2">
                            Oups !
                        </h3>
                        <p className="text-slate-500 text-sm font-medium mb-6 max-w-xs">
                            {errorMessage}
                        </p>
                        <div className="flex gap-3 w-full">
                            <button
                                onClick={() => { setStep('form'); setErrorMessage(''); }}
                                className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-colors"
                            >
                                Réessayer
                            </button>
                            <button
                                onClick={onClose}
                                className="flex-1 py-3 rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 transition-colors"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ArchiveSearchModal;
